import { ReactNode, useRef, useState } from "react";

import { createPortal } from "react-dom";

import { PortalContainerProvider } from "@/context/PortalContainerContext";

import { cn } from "@utils/ui";

import { FloatingPanelMode, PanelHeader } from "./PanelHeader";
import { useDragPosition } from "./useDragPosition";
import { ResizeEdge, usePanelResize } from "./usePanelResize";
import { usePopoutWindow } from "./usePopoutWindow";

type FloatingPanelProps = {
  onClose: () => void;
  title: string;
  subtitle?: ReactNode;
  /** Shown before the title. */
  icon?: ReactNode;
  /** Initial floating width in px. */
  width?: number;
  footer?: ReactNode;
  children: ReactNode;
};

const POPOUT_HEIGHT = 900;

// Invisible grab strips straddling the border, mostly outside it so they stay
// clear of the body's scrollbar.
const RESIZE_HANDLE_CLASSES: Record<ResizeEdge, string> = {
  right: "-right-1.5 inset-y-3 w-2 cursor-ew-resize",
  bottom: "-bottom-1.5 inset-x-3 h-2 cursor-ns-resize",
  corner: "-bottom-1.5 -right-1.5 size-4 cursor-nwse-resize",
  left: "-left-1.5 inset-y-3 w-2 cursor-ew-resize",
};

/**
 * A non-modal panel that floats over the page (drag by its header, resize by
 * its edges), docks to the right edge (resize by its left edge), minimises to
 * its header, or pops out into a separate browser window (MDRS-486). Its
 * position is `fixed`, so it can be mounted anywhere.
 *
 * Render it only while open: unmounting also closes any pop-out window.
 */
export const FloatingPanel = ({
  onClose,
  title,
  subtitle,
  icon,
  width = 820,
  footer,
  children,
}: FloatingPanelProps) => {
  const [isDocked, setIsDocked] = useState(false);
  const [isMinimised, setIsMinimised] = useState(false);
  const panelRef = useRef<HTMLElement>(null);
  const { floatingSize, dockedWidth, startResize } = usePanelResize(width, panelRef);
  const { position, handlePointerDown } = useDragPosition(floatingSize.width);
  const { popoutContainer, openPopout, closePopout } = usePopoutWindow(title);
  const mode: FloatingPanelMode = popoutContainer
    ? "popout"
    : isDocked
      ? "docked"
      : "floating";
  const showsBody = !isMinimised && !popoutContainer;

  const handlePopout = () => {
    const opened = openPopout({
      width: floatingSize.width,
      height: floatingSize.height ?? POPOUT_HEIGHT,
    });
    if (opened) {
      // Coming back — by button or by the user closing the window — lands it
      // floating and expanded.
      setIsMinimised(false);
      setIsDocked(false);
    }
  };

  const header = (
    <PanelHeader
      title={title}
      subtitle={subtitle}
      icon={icon}
      mode={mode}
      isMinimised={isMinimised}
      onToggleMinimised={() => setIsMinimised((value) => !value)}
      onToggleDocked={() => setIsDocked((value) => !value)}
      onPopout={handlePopout}
      onBringBack={closePopout}
      onClose={onClose}
      onDragStart={handlePointerDown}
    />
  );

  const body = (
    <>
      <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
      {footer && <div className="border-t border-border px-5 py-3">{footer}</div>}
    </>
  );

  const resizeHandle = (edge: ResizeEdge) => (
    <div
      aria-hidden
      data-testid={`floating-panel-resize-${edge}`}
      onPointerDown={startResize(edge)}
      className={cn("absolute touch-none", RESIZE_HANDLE_CLASSES[edge])}
    />
  );

  return (
    <>
      <section
        ref={panelRef}
        aria-label={title}
        data-mode={mode}
        className={cn(
          "fixed z-40 flex flex-col rounded-xl border border-border bg-background-white shadow-lg",
          mode === "docked"
            ? // Minimised, it's only as tall as its header.
              cn(
                "right-2 top-2 max-w-[calc(100vw-1rem)]",
                !isMinimised && "bottom-2"
              )
            : "max-h-[calc(100dvh-2rem)] max-w-[calc(100vw-2rem)]"
        )}
        style={
          mode === "docked"
            ? { width: dockedWidth }
            : {
                left: position.left,
                top: position.top,
                width: floatingSize.width,
                height: showsBody ? floatingSize.height : undefined,
              }
        }
      >
        {/* Clips the content to the rounded corners, leaving the resize
            handles free to overhang the border. */}
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[inherit]">
          {header}
          {showsBody && body}
        </div>
        {showsBody &&
          (mode === "docked" ? (
            resizeHandle("left")
          ) : (
            <>
              {resizeHandle("right")}
              {resizeHandle("bottom")}
              {resizeHandle("corner")}
            </>
          ))}
      </section>

      {popoutContainer &&
        createPortal(
          <PortalContainerProvider value={popoutContainer}>
            <section
              aria-label={title}
              className="flex h-full flex-col bg-background-white"
            >
              {header}
              {body}
            </section>
          </PortalContainerProvider>,
          popoutContainer
        )}
    </>
  );
};
